import { API_URL } from "../../constants/constants";
import { ResponseDataType, listDataType } from "../../types";
import { apiSlice } from "../api/apiSlice";

const apiWithTags = apiSlice.enhanceEndpoints({
    addTagTypes: [
        "boxCollaboratorHistory",
        "boxCollaboratorList",
        "boxCollaboratorDetails",
        "boxCollaborationDetails",
    ],
});

export const boxSlice = apiWithTags.injectEndpoints({
    endpoints: (builder) => ({
        /**
         * Box collaborator list query function.
         *
         * @param search - The search query for filtering users.
         * @param limit - The maximum number of users to fetch.
         * @param current_page - The current page number for pagination.
         * @param sort_by - The field to sort the users by.
         * @param sort_direction - The direction to sort the users in.
         * @returns A promise that resolves to the response data.
         */
        boxCollaboratorList: builder.query<ResponseDataType, listDataType>({
            query: ({
                search,
                limit,
                current_page,
                sort_by,
                sort_direction,
                status,
                domains,
            }) => ({
                url:
                    API_URL.BOX_COLLABORATIONS +
                    `?${limit ? `limit=${limit}` : ""}${current_page ? `&current_page=${current_page}` : ""
                    }${sort_by ? `&sort_by=${sort_by}` : ""}${sort_direction ? `&sort_direction=${sort_direction}` : ""
                    }${search ? `&search=${encodeURIComponent(search)}` : ""}${status ? `&status=${status}` : ""
                    }${domains ? `&domains=${domains}` : ""}`,
                method: "GET",
            }),
            providesTags: ["boxCollaboratorList"],
        }),
        /**
         * Box collaborator history query function.
         * @param search - The search query for filtering users.
         * @param limit - The maximum number of users to fetch.
         * @param current_page - The current page number for pagination.
         * @param sort_by - The field to sort the users by.
         * @param sort_direction - The direction to sort the users in.
         * @returns A promise that resolves to the response data.
         */

        boxCollaboratorHistory: builder.query<ResponseDataType, listDataType>({
            query: ({
                search,
                limit,
                current_page,
                sort_by,
                sort_direction,
                start_date,
                end_date,
            }) => ({
                url:
                    API_URL.BOX_COLLABORATORS +
                    `?${limit ? `limit=${limit}` : ""}${current_page ? `&current_page=${current_page}` : ""
                    }${sort_by ? `&sort_by=${sort_by}` : ""}${sort_direction ? `&sort_direction=${sort_direction}` : ""
                    }${search ? `&search=${encodeURIComponent(search)}` : ""}${start_date ? `&start_date=${start_date}` : ""
                    }${end_date ? `&end_date=${end_date}` : ""}`,
                method: "GET",
            }),
            providesTags: ["boxCollaboratorHistory"],
        }),

        /**
         * Box collaborator details query function.
         *
         * @param id - The id of the user to fetch.
         * @returns A promise that resolves to the response data.
         */
        boxCollaboratorDetails: builder.query<ResponseDataType, listDataType>({
            query: ({
                email,
                search,
                limit,
                current_page,
                sort_by,
                sort_direction,
            }) => ({
                url:
                    API_URL.BOX_COLLABORATORS +
                    `/${email} ?${limit ? `limit=${limit}` : ""}${current_page ? `&current_page=${current_page}` : ""
                    }${sort_by ? `&sort_by=${sort_by}` : ""}${sort_direction ? `&sort_direction=${sort_direction}` : ""
                    }${search ? `&search=${encodeURIComponent(search)}` : ""}`,
                method: "GET",
            }),
            providesTags: ["boxCollaboratorDetails"],
        }),
        /**
         * Box collaboration details query function.
         * @param email - The email of the user to fetch.
         * @param search - The search query for filtering users.
         * @param limit - The maximum number of users to fetch.
         * @param current_page - The current page number for pagination.
         * @param sort_by - The field to sort the users by.
         * @param sort_direction - The direction to sort the users in.
         * @returns A promise that resolves to the response data.
         */
        boxCollaborationDetails: builder.query<ResponseDataType, listDataType>({
            query: ({
                email,
                search,
                limit,
                current_page,
                sort_by,
                sort_direction,
            }) => ({
                url:
                    API_URL.BOX_COLLABORATIONS +
                    `/${email} ?${limit ? `limit=${limit}` : ""}${current_page ? `&current_page=${current_page}` : ""
                    }${sort_by ? `&sort_by=${sort_by}` : ""}${sort_direction ? `&sort_direction=${sort_direction}` : ""
                    }${search ? `&search=${encodeURIComponent(search)}` : ""}`,
                method: "GET",
            }),
            providesTags: ["boxCollaborationDetails"],
        }),

        /**
         * Sync collaborator mutation function.
         *
         * @param formData - The form data to create a new collaborator.
         * @returns A promise that resolves to the response data.
         */

        syncCollaborator: builder.mutation<ResponseDataType, FormData>({
            query: (formData) => ({
                url: API_URL.BOX_COLLABORATORS,
                method: "POST",
                body: formData,
            }),
            invalidatesTags: [
                "boxCollaboratorList",
                "boxCollaboratorDetails",
                "boxCollaborationDetails",
                "boxCollaboratorHistory",
            ],
        }),
    }),
});

export const {
    useBoxCollaboratorHistoryQuery,
    useBoxCollaboratorListQuery,
    useBoxCollaboratorDetailsQuery,
    useBoxCollaborationDetailsQuery,
    useSyncCollaboratorMutation,
} = boxSlice;
